/**
 * React Query hooks for Known Page Analysis jobs
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { API_URL } from '../../config';

type KnownPageAnalysisJob = {
  id: string;
  status: 'queued' | 'running' | 'completed' | 'failed';
  createdAt: string;
  startedAt?: string | null;
  completedAt?: string | null;
  failedAt?: string | null;
  lastError?: string | null;
  childJobStats: {
    total: number;
    queued: number;
    running: number;
    completed: number;
    failed: number;
  };
};

type KnownPageAnalysisJobDetails = {
  id: string;
  status: 'queued' | 'running' | 'completed' | 'failed';
  payload: {
    propertyId: string;
    credentialId?: string | null;
  };
  createdAt: string;
  startedAt?: string | null;
  completedAt?: string | null;
  failedAt?: string | null;
  lastError?: string | null;
  metadata?: Record<string, unknown>;
};

export type ChildJob = {
  id: string;
  status: 'queued' | 'running' | 'completed' | 'failed';
  payload: {
    canonicalPageId: string;
    credentialId?: string | null;
    qaReportId: string;
  };
  createdAt: string;
  startedAt?: string | null;
  completedAt?: string | null;
  failedAt?: string | null;
  lastError?: string | null;
  qaReport?: {
    id: string;
    status: 'pending' | 'running' | 'issues_found' | 'no_issues' | 'failed';
    hasIssues: boolean;
    issuesFoundCount: number;
    details?: Record<string, unknown> | null;
  } | null;
};

type PaginationMetadata = {
  page: number;
  pageSize: number;
  totalItems: number;
  totalPages: number;
};

/**
 * Fetch list of known page analysis jobs for a property
 */
function _useKnownPageAnalysisJobs(
  propertyId: string,
  options?: { page?: number; pageSize?: number }
) {
  const page = options?.page ?? 1;
  const pageSize = options?.pageSize ?? 20;

  return useQuery({
    queryKey: ['known-page-analysis', propertyId, page, pageSize],
    queryFn: async (): Promise<{
      jobs: KnownPageAnalysisJob[];
      pagination: PaginationMetadata;
    }> => {
      const params = new URLSearchParams({
        page: String(page),
        pageSize: String(pageSize),
      });

      const response = await fetch(
        `${API_URL}/properties/${propertyId}/known-page-analysis?${params.toString()}`,
        {
          credentials: 'include',
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch known page analysis jobs');
      }

      const data = await response.json();
      return {
        jobs: data.jobs || [],
        pagination: data.pagination || { page: 1, pageSize: 20, totalItems: 0, totalPages: 0 },
      };
    },
  });
}

/**
 * Fetch details for a specific known page analysis job
 */
export function useKnownPageAnalysisJobDetails(jobId: string | undefined) {
  return useQuery({
    queryKey: ['known-page-analysis-details', jobId],
    queryFn: async (): Promise<{
      job: KnownPageAnalysisJobDetails;
      childJobs: ChildJob[];
      jobStats: {
        total: number;
        queued: number;
        running: number;
        completed: number;
        failed: number;
      };
      qaStats: {
        total: number;
        pending: number;
        running: number;
        issues_found: number;
        no_issues: number;
        failed: number;
      };
    }> => {
      if (!jobId) throw new Error('Job ID is required');

      const response = await fetch(`${API_URL}/known-page-analysis/${jobId}`, {
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('Failed to fetch job details');
      }

      const data = await response.json();
      // API returns { ok: true, job, childJobs, jobStats, qaStats }
      // Return just the data portion without the ok wrapper
      return {
        job: data.job,
        childJobs: data.childJobs,
        jobStats: data.jobStats,
        qaStats: data.qaStats,
      };
    },
    enabled: !!jobId,
    refetchInterval: (query) => {
      // Refetch every 5 seconds if any child jobs are still active
      const data = query.state.data;
      if (!data) return false;

      // Check if any child jobs are still running or queued
      const hasActiveJobs = data.jobStats.running > 0 || data.jobStats.queued > 0;

      // Also check parent job in case it's still starting up
      const parentActive = data.job.status === 'running' || data.job.status === 'queued';

      if (hasActiveJobs || parentActive) {
        return 5000; // Poll every 5 seconds
      }
      return false; // Stop polling when all jobs complete
    },
  });
}

/**
 * Start a new known page analysis
 */
function _useStartKnownPageAnalysis(propertyId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (options?: {
      credentialId?: string | null;
    }): Promise<{ ok: boolean; job: { id: string } }> => {
      const response = await fetch(`${API_URL}/properties/${propertyId}/known-page-analysis`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          credentialId: options?.credentialId ?? null,
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || 'Failed to start known page analysis');
      }

      return response.json();
    },
    onSuccess: () => {
      // Invalidate all known page analysis queries to refresh the list
      queryClient.invalidateQueries({ queryKey: ['known-page-analysis', propertyId] });
    },
  });
}
